import React, { useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import { usePathname } from "../../shared/hooks/useNavigation";
import { useRouter } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import { Nav, Navbar } from "react-bootstrap";
import styled from "styled-components";

import { useAppState } from "../../contexts/ApplicationStateProvider";
import { useStores } from "../../contexts/StoreContext";
import breakpoints from "../../shared/constants";
import useDevice from "../../shared/customHooks/useDevice";
import StyledLink from "./StyledLink";

const SubMenuWrapper = styled.div`
  display: none;
  position: absolute;
  width: fit-content;
  height: 100%;
  top: 80%;
  z-index: 2;
  background: ${({ theme, $navigationBackgroundColor }) =>
    $navigationBackgroundColor || theme.primaryColor};
  color: ${({ theme }) => theme.textColor};
`;

const SubMenuItem = styled.div`
  padding: 1rem;
  font-size: 1rem;
  height: 100%;
`;

const StyledToggle = styled(Navbar.Toggle)`
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  display: ${({ $isMobile }) => ($isMobile ? "unset !important" : "none")};
`;

const StyledMenuItem = styled(Nav.Item)`
  font-size: 2rem;
  text-align: center;
  z-index: 1;

  border-top: ${({ $isMobile }) =>
    $isMobile ? "1px solid hsla(0,0%,100%,.7)" : "none"};

  width: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};

  &:hover ${SubMenuWrapper} {
    display: flex;
  }

  @media (max-width: ${breakpoints.DESKTOP}) {
    justify-content: center;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    font-size: 1rem;
  } ;
`;

const StyledReturnButton = styled.button`
  border: none;
  outline: none;
  background: none;
  font-size: 1.2rem;
  position: relative;
  color: ${({ theme }) => theme.textColor};

  &:hover,
  &:active {
    color: ${({ theme }) => theme.primaryColor};
  }
`;

const StyledNavLink = styled.div`
  position: relative;

  #nav-wrapper {
    display: ${({ $isMobile }) => ($isMobile ? "none" : "unset")};
  }

  .show #nav-wrapper {
    display: ${({ $isMobile }) => ($isMobile ? "block" : "unset")};
  }

  ${({ $isMobile }) =>
    $isMobile
      ? `
    #navbar.show + #menu-backdrop {
      display: block;
    }
  `
      : ""}
`;

const MenuBackdrop = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 11;
`;

const StyledCollapse = styled(Navbar.Collapse)`
  position: ${({ $isMobile }) => ($isMobile ? "absolute" : "unset")};
  right: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  left: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  top: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "unset")};
`;

const Wrapper = styled.div`
  height: ${({ $isMobile }) => ($isMobile ? "100vh" : "100%")};
  left: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  top: ${({ $isMobile }) => ($isMobile ? "0" : "unset")};
  width: ${({ $isMobile }) => ($isMobile ? "100vw" : "unset")};
  background: ${({ $isMobile, $navigationBackgroundColor }) => {
    let color = "unset";
    if ($isMobile) {
      if ($navigationBackgroundColor) {
        color = $navigationBackgroundColor;
      } else {
        color = "rgba(22, 25, 26, 0.95)";
      }
    }
    return $navigationBackgroundColor || color;
  }};
  overflow: ${({ $isMobile }) => ($isMobile ? "auto" : "unset")};
  display: ${({ $isMobile }) => ($isMobile ? "flex" : "unset")};
  flex-direction: ${({ $isMobile }) => ($isMobile ? "column" : "unset")};
  padding: ${({ $isMobile }) => ($isMobile ? "30px 30px 50px" : "unset")};
  justify-content: ${({ $isMobile }) => ($isMobile ? "center" : "unset")};
  position: ${({ $isMobile }) => ($isMobile ? "absolute" : "unset")};
  z-index: ${({ $isMobile }) => ($isMobile ? "12" : "unset")};
`;

function NavLinks(props) {
  const { navigationBackgroundColor, className, onLinkClick, isAlwaysMobileView } = props;
  const router = useRouter();
  const currentPath = usePathname();
  const { isMobile, isSmallScreen } = useDevice();
  const isMobileView = isMobile || isSmallScreen || isAlwaysMobileView;

  const { setIsInteractive, isInteractive } = useAppState();
  const { navigationStore, homepageStore } = useStores();
  const { topNavigation } = navigationStore;
  const { hasVideos } = homepageStore;

  const [selectedParent, setSelectedParent] = useState(null);
  const returnToStatic = () => {
    setIsInteractive(false);
    router.push("/");
  };

  const handleNavigation = (href) => {
    // First, tell the parent to close the navbar
    onLinkClick();
    // Then, navigate to the page
    router.push(href);
  };

  if (isEmpty(topNavigation)) {
    return null;
  }

  const handleClickParent = (parentItem) => {
    setSelectedParent(parentItem);
  };

  const handleClickBack = (e) => {
    setSelectedParent(null);
    e.stopPropagation();
    e.preventDefault();
    return false;
  };

  const renderMobileMenu = () => {
    if (selectedParent) {
      return (
        <StyledMenuItem
          className={`ms-lg-4 text-black ${
            selectedParent.children && selectedParent.children.length > 0
              ? "has-children"
              : ""
          }`}
          id="second-level-navigation-menu-selected"
          $isMobile={isMobileView}
        >
          <StyledReturnButton onClick={(e) => handleClickBack(e)}>
            <Image
              alt="go back a page"
              height={20}
              objectFit="contain"
              src="/arrow-border-left-white.svg"
              width={20}
            />
            <span className="fs-1 ps-5">{selectedParent.title}</span>
          </StyledReturnButton>
          {selectedParent.children.map((child, index) => (
            <StyledLink
              key={child.id}
              className="text-uppercase"
              eventKey={`${child.path}-${index}`}
              href={child.path ? `/${child.path}` : (child.externalPath || "/")}
              isMobile={isMobileView}
              page={child}
              backgroundColour={navigationBackgroundColor}
            >
              {child.title}
            </StyledLink>
          ))}
        </StyledMenuItem>
      );
    }

    return topNavigation.map((child, index) => {
      const href = child.path ? `/${child.path}` : (child.externalPath || "/");
      return (
        <StyledMenuItem
          key={child.id}
          id="second-level-navigation-menu-selected"
          $isMobile={isMobileView}
          className={`ms-lg-4 ${
            child.children && child.children.length > 0 ? "has-children" : ""
          }`}
        >
          <StyledLink
            active={currentPath.includes(child.path)}
            eventKey={`${child.path}-${index}`}
            href={href}
            isMobile={isMobileView}
            navigationBackgroundColor={navigationBackgroundColor}
            onClick={
              child.children.length > 0
                ? () => handleClickParent(child)
                : () => handleNavigation(href)
            }
            page={child}
            backgroundColour={navigationBackgroundColor}
          >
            {child.title}
          </StyledLink>
        </StyledMenuItem>
      );
    });
  };

  const renderDesktopMenu = () => {
    return topNavigation.map((page, index) => {
      const href = page.path ? `/${page.path}` : (page.externalPath || "/");

      return (
        <StyledMenuItem
          key={`${href}-${page?.title}`}
          className={`ms-lg-4 d-flex ${
            page.children && page.children.length > 0 ? "has-children" : ""
          }`}
          id="navigation-menu-desktop"
        >
          <StyledLink
            active={currentPath.includes(page.path)}
            className="text-uppercase"
            eventKey={`${page.path}-${index}`}
            href={href}
            navigationBackgroundColor={navigationBackgroundColor}
            onClick={page.disable_route ? () => {} : undefined}
            page={page}
            backgroundColour={navigationBackgroundColor}
          >
            {page.title}
          </StyledLink>
          {page.children && page.children.length > 0 && (
            <SubMenuWrapper
              id="second-level-navigation-menu-desktop"
              $navigationBackgroundColor={navigationBackgroundColor}
            >
              {page.children.map((subItem) => {
                const childHref = subItem.path
                  ? `/${subItem.path}`
                  : (subItem.externalPath || "/");

                return (
                  <SubMenuItem key={`${subItem.path}`}>
                    <StyledLink
                      active={currentPath.includes(subItem.path)}
                      className="text-uppercase h-100"
                      eventKey={`${subItem.path}-${index}`}
                      href={childHref}
                      navigationBackgroundColor={navigationBackgroundColor}
                      page={subItem}
                    >
                      {subItem.title}
                    </StyledLink>
                  </SubMenuItem>
                );
              })}
            </SubMenuWrapper>
          )}
        </StyledMenuItem>
      );
    });
  };
  
  return (
    <StyledNavLink className={className} $isMobile={isMobileView}>
      <StyledToggle
        aria-controls="navbar"
        className="h-100"
        $isMobile={isMobileView}
      />
      <StyledCollapse
        className="justify-content-end h-100"
        id="navbar"
        $isMobile={isMobileView}
      >
        <Wrapper
          id="nav-wrapper"
          $isMobile={isMobileView}
          $navigationBackgroundColor={navigationBackgroundColor}
        >
          <Nav
            className={`${
              isMobileView ? "d-flex flex-column" : ""
            } h-100`}
          >
            {isMobileView
              ? renderMobileMenu()
              : renderDesktopMenu()}

            {isMobile && isInteractive && hasVideos && (
              <StyledMenuItem className="ms-lg-4">
                <StyledReturnButton className="pt-5" onClick={returnToStatic}>
                  Take me to the normal site
                </StyledReturnButton>
              </StyledMenuItem>
            )}
          </Nav>
        </Wrapper>
      </StyledCollapse>
      <MenuBackdrop id="menu-backdrop" onClick={onLinkClick} />
    </StyledNavLink>
  );
}

NavLinks.defaultProps = {
  className: "",
  isAlwaysMobileView: false,
  navigationBackgroundColor: "#fff",
};

NavLinks.propTypes = {
  className: PropTypes.string,
  isAlwaysMobileView: PropTypes.bool,
  navigationBackgroundColor: PropTypes.string,
};

export default observer(NavLinks);
